import { sizes } from '@lmc/common/constants'
import type { Home } from '@lmc/ts-common'
import {
  Button,
  Chevron,
  Edit,
  EmptyState,
  HeaderSection,
  Heading,
  Icon,
  NoDataIcon,
  Popover,
  Select,
  Table,
  ThreeDots
} from '@lmc/ui-kit'
import { Text } from '@lmc/ui-kit/src/general'
import { NeedCategoryDescriptionEnum } from '@lmc/validations/lib/schemas/v6/needs'
import { Dropdown, Pagination } from 'antd'
import { useAbilityAccess } from 'components/AccessControl'
import { ProTag } from 'components/ProTag'
import useNeeds, { type NeedItem, type UseNeedsParams } from 'hooks/useNeeds'
import { type FC, useState } from 'react'
import { useTranslation } from 'react-i18next'
import {
  connect,
  type MapDispatchToPropsParam,
  type MapStateToPropsParam,
  useSelector
} from 'react-redux'
import styled from 'styled-components'
import { isGroupUser } from 'utils'
import { Spinner } from '../../../components/generic'
import { AddNeedModal } from './components/AddNeedModal'
import { DeleteNeedModal } from './components/DeleteNeedModal'
import { EditNeedModal } from './components/EditNeedModal'

const SORT_OPTIONS = [
  {
    sortBy: 'createdOn' as const,
    sortOrder: 'desc' as const,
    labelKey: 'skillsAndNeeds.sortNewestFirst'
  },
  {
    sortBy: 'createdOn' as const,
    sortOrder: 'asc' as const,
    labelKey: 'skillsAndNeeds.sortOldestFirst'
  },
  {
    sortBy: 'name' as const,
    sortOrder: 'asc' as const,
    labelKey: 'skillsAndNeeds.sortNameAZ'
  },
  {
    sortBy: 'name' as const,
    sortOrder: 'desc' as const,
    labelKey: 'skillsAndNeeds.sortNameZA'
  }
]

interface StateProps {
  preferredPeopleNominative?: string
  carePlanNominative?: string
}

type Props = StateProps

export const NeedTemplateScreen: FC<Props> = ({
  preferredPeopleNominative,
  carePlanNominative
}) => {
  const { t } = useTranslation()

  const [open, setOpen] = useState(false)
  const [editNeed, setEditNeed] = useState<NeedItem | null>(null)
  const [deleteNeed, setDeleteNeed] = useState<NeedItem | null>(null)
  const [currentPage, setCurrentPage] = useState(1)
  const [sortIndex, setSortIndex] = useState(0)

  const sortOption = SORT_OPTIONS[sortIndex]
  const needsParams: UseNeedsParams = {
    page: currentPage,
    limit: 10,
    sortBy: sortOption.sortBy,
    sortOrder: sortOption.sortOrder
  }

  const { needs, pagination, isLoading } = useNeeds(needsParams)

  const handleToggleModal = () => {
    setOpen(!open)
  }

  const home = useSelector(
    (state: { global: { home: Home } }) => state.global.home
  )
  const homeGroup = useSelector(
    (state: { global: { homeGroup?: { id?: string } } }) =>
      state.global?.homeGroup
  )
  const user = useSelector(
    (state: { global: { user: { role: string } } }) => state.global.user
  )

  const isHomeGroup = !!(home?.homeGroup || homeGroup?.id)
  const isGroupRole = isGroupUser(user?.role)
  const hasAbility = useAbilityAccess([
    { i: 'create', this: 'SkillNeedTemplate' }
  ])

  const allowCreateEditTemplates =
    (isHomeGroup && isGroupRole && hasAbility) || (!isHomeGroup && hasAbility)

  const hasNeeds = needs.length > 0

  const sortingOptions = SORT_OPTIONS.map((opt, index) => ({
    label: t(opt.labelKey),
    value: String(index)
  }))

  const columns = [
    {
      title: t('skillsAndNeeds.needColumn'),
      dataIndex: 'name',
      key: 'name',
      width: '45%'
    },
    {
      title: t('skillsAndNeeds.needsCategoryColumn'),
      dataIndex: 'modsCategory',
      key: 'modsCategory',
      width: '45%',
      render: (value: string) =>
        (value &&
          NeedCategoryDescriptionEnum[
            value as keyof typeof NeedCategoryDescriptionEnum
          ]) ||
        value ||
        '-'
    },
    ...(allowCreateEditTemplates
      ? [
          {
            title: t('skillsAndNeeds.actionsColumn'),
            key: 'actions',
            width: 150,
            render: (_: unknown, _record: NeedItem) => (
              <StyledActionsCell onClick={(e) => e.stopPropagation()}>
                <StyledEditButton
                  variant="default"
                  onClick={() => {
                    setEditNeed(_record)
                  }}
                >
                  <Edit />
                  {t('skillsAndNeeds.editNeed')}
                </StyledEditButton>
                <Dropdown
                  menu={{
                    items: [
                      {
                        key: 'delete',
                        danger: true,
                        label: t('skillsAndNeeds.deleteNeedAction'),
                        onClick: () => {
                          setDeleteNeed(_record)
                        }
                      }
                    ],
                    style: { borderRadius: 20 }
                  }}
                  trigger={['click']}
                  placement="bottomRight"
                  align={{ offset: [-4, 16] }}
                >
                  <StyledDotsButton>
                    <ThreeDots />
                  </StyledDotsButton>
                </Dropdown>
              </StyledActionsCell>
            )
          }
        ]
      : [])
  ]

  const dataSource = needs.map((need) => ({
    ...need,
    key: need.id
  }))

  const renderButton = (
    <div>
      <Button
        disabled={!allowCreateEditTemplates}
        icon={<Icon name="PlusOutlined" />}
        variant="primary"
        onClick={handleToggleModal}
        style={{ fontWeight: 400 }}
      >
        {t('skillsAndNeeds.addNewNeed')}
      </Button>
    </div>
  )

  return (
    <div style={styles.container}>
      <StyledHeaderWrapper>
        <HeaderSection
          title={
            <StyledFlexContainer>
              {t('skillsAndNeeds.needTemplatesTitle')}
              <ProTag />
            </StyledFlexContainer>
          }
          subtitle={t('skillsAndNeeds.needTemplatesSubtitle', {
            serviceUsers: preferredPeopleNominative?.toLowerCase(),
            carePlans: carePlanNominative
          })}
          subtitleStyle={{ marginTop: 8 }}
          rightChildren={
            !allowCreateEditTemplates ? (
              <Popover
                placement="bottomRight"
                content={
                  <StyledLinkWrapper>
                    <Text level={1.5}>{t('skillsAndNeeds.tableReadOnly')}</Text>
                    <div>
                      <StyledHeading level={5}>
                        {t('skillsAndNeeds.tableReadOnlyDescription')}
                      </StyledHeading>
                      <StyledLink level={5}>
                        <StyledAnchor
                          href="https://support.logmycare.co.uk/en/articles/6987442-how-to-set-up-skills-for-your-service"
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          {t('skillsAndNeeds.tableReadOnlyLink')}
                        </StyledAnchor>
                      </StyledLink>
                    </div>
                  </StyledLinkWrapper>
                }
              >
                {renderButton}
              </Popover>
            ) : (
              renderButton
            )
          }
        />
      </StyledHeaderWrapper>
      {isLoading ? (
        <Spinner />
      ) : !hasNeeds ? (
        <EmptyState
          title=""
          icon={<NoDataIcon />}
          description={t('skillsAndNeeds.emptyNeedsText')}
        >
          {allowCreateEditTemplates && (
            <Button
              onClick={handleToggleModal}
              icon={<Icon name="PlusOutlined" />}
              variant="primary"
            >
              {t('skillsAndNeeds.addNewNeed')}
            </Button>
          )}
        </EmptyState>
      ) : (
        <>
          <StyledTableContainer>
            <StyledTableHeader>
              <StyledNeedsCount level={1}>
                {t('skillsAndNeeds.needsCount', {
                  current: needs.length,
                  total: pagination?.totalItems ?? needs.length
                })}
              </StyledNeedsCount>
              <StyledSortWrapper>
                <StyledSortLabel weight="bold">
                  {t('skillsAndNeeds.sortBy')}
                </StyledSortLabel>
                <StyledSortSelect
                  placeholder={
                    <StyledOptionText weight="regular">
                      {sortingOptions[0].label}
                    </StyledOptionText>
                  }
                  suffixIcon={<Chevron direction="down" />}
                  onChange={(value) => {
                    setSortIndex(Number(value))
                    setCurrentPage(1)
                  }}
                >
                  {sortingOptions.map((option) => (
                    <Select.Option
                      key={option.value}
                      value={option.value}
                      label={option.label}
                    >
                      <StyledOptionText weight="regular">
                        {option.label}
                      </StyledOptionText>
                    </Select.Option>
                  ))}
                </StyledSortSelect>
              </StyledSortWrapper>
            </StyledTableHeader>
            <Table
              dataSource={dataSource}
              columns={columns}
              pagination={false}
            />
          </StyledTableContainer>
          <StyledPaginationWrapper>
            <Pagination
              current={currentPage}
              pageSize={10}
              total={pagination?.totalItems ?? 0}
              onChange={(page) => setCurrentPage(page)}
              showSizeChanger={false}
            />
          </StyledPaginationWrapper>
        </>
      )}
      <AddNeedModal isModalVisible={open} onClose={handleToggleModal} />
      <EditNeedModal
        isModalVisible={!!editNeed}
        onClose={() => setEditNeed(null)}
        need={editNeed}
      />
      <DeleteNeedModal
        isModalVisible={!!deleteNeed}
        onClose={() => setDeleteNeed(null)}
        need={deleteNeed}
        preferredPeopleNominative={preferredPeopleNominative}
      />
    </div>
  )
}

const StyledFlexContainer = styled.div`
  display: flex;
  align-items: center;
`

const StyledLinkWrapper = styled.div`
  display: flex;
  flex-direction: column;
  max-width: 257px;
`

const StyledHeading = styled(Heading)`
  font-size: 16px;
  font-weight: 500;
`

const StyledLink = styled(Heading)`
  font-size: 16px;
  font-weight: 500;
  margin-left: 5px;
`

const StyledAnchor = styled.a`
  color: black;
  text-decoration: underline;
`

const StyledNeedsCount = styled(Text)`
  color: ${({ theme }) => theme.color.neutral.color4} !important;
`

const StyledTableContainer = styled.div`
  overflow: hidden;
  border-radius: 20px;

  .ant-table-thead > tr > th {
    padding-top: 8px !important;
    padding-bottom: 8px !important;
    color: ${({ theme }) => theme.color.neutral.color4} !important;
    font-weight: 500 !important;
  }

  .ant-table-tbody > tr:nth-child(odd) > td {
    background: ${({ theme }) => theme.color.neutral.color11};
  }

  .ant-table-tbody > tr:nth-child(even) > td {
    background: ${({ theme }) => theme.color.neutral.color10};
  }

  .ant-table-tbody > tr > td {
    color: ${({ theme }) => theme.color.neutral.color3} !important;
    font-weight: 500 !important;
    vertical-align: top !important;
    font-size: 15px !important;
  }
`

const StyledPaginationWrapper = styled.div`
  display: flex;
  justify-content: center;
  margin-top: 16px;
`

const StyledTableHeader = styled.div`
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  margin-bottom: 16px;
`

const StyledSortWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`

const StyledSortLabel = styled(Text)`
  color: ${({ theme }) => theme.color.neutral.color3};
`

const StyledSortSelect = styled(Select)`
  width: 290px;
` as typeof Select

const StyledOptionText = styled(Text)`
  color: ${({ theme }) => theme.color.neutral.color4};
`

const StyledActionsCell = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`

const StyledDotsButton = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
`

const StyledEditButton = styled(Button)`
  gap: 2px;
  padding: 6px 15px;
  margin-right: 8px;
`

const StyledHeaderWrapper = styled.div`
  margin-bottom: 10px;

  [data-testid="header-section"] > .ant-row > div {
    flex-wrap: nowrap !important;
    padding: 20px 28px !important;
  }

  [data-testid="header-section-right-children"] {
    margin-left: 40px;
    flex-shrink: 0;
  }

  [data-testid="header-section-title-container"] {
    min-width: 0;
  }

  [data-testid="header-section-subtitle"] {
    white-space: normal;
    word-break: break-word;
  }
`

const styles = {
  container: {
    margin: '0 auto',
    maxWidth: sizes.childScreen,
    padding: '24px 24px'
  },
  tableContainer: { overflow: 'auto' },
  marginBottom: { marginBottom: 10 }
}

const mapStateToProps = (state: { global: { home: Home } }) => ({
  preferredPeopleNominative: state.global.home.preferredPeopleNominative,
  carePlanNominative: state.global.home.preferredCarePlansNominative
})

export default connect<StateProps, Record<string, never>, Props>(
  mapStateToProps as MapStateToPropsParam<StateProps, Props, unknown>,
  {} as MapDispatchToPropsParam<Record<string, never>, Props>
)(NeedTemplateScreen)
